import {
  ArrowUp,
  Square,
  XCircle,
  LoaderCircle,
  CheckCircle,
  Cog,
  Paperclip,
  AlertCircle,
  Plug,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, useLayoutEffect } from "react";
import TextareaAutosize from "react-textarea-autosize";
import useFileUpload, { getDisplayFileName } from "../../hooks/useFileUpload";
import { mergeTailwindClasses } from "../../helpers/tailwindClassHelper";
import { formatFileSize } from "../../helpers/fileSizeHelper";
import BrainBankSyncStrip from "../KnowledgeBase/BrainBankSyncStrip";
import FileIcon from "./FileIcon";
import { toast } from "sonner";
import { useHat } from "../../context/HatContext";
import EnhancedAttach from "../Message/EnhancedAttach";
import { useComposerBrainBankSync } from "../../hooks/useComposerBrainBankSync";
import DictateComponent from "../Dictate/DictateComponent";
import { useFileTransfer } from "../../context/FileTransferContext";
import { useUser } from "../../context/UserContext";
import { useModel } from "../../context/ModelContext";
import { getConnectorInfo, checkGoogleDriveConnection, checkGoogleEmailConnection, checkGoogleCalendarConnection, checkOutlookConnection, authenticateGoogleDrive, authenticateGoogleEmail, authenticateGoogleCalendar, authenticateOutlook } from "../../service/apiService";
import GoogleDriveAuth from "../GoogleDrive/GoogleDriveAuth";
import GoogleDriveIcon from "../../icons/googleDrive";
import GoogleEmailAuth from "../GoogleEmail/GoogleEmailAuth";
import GoogleEmailIcon from "../../icons/googleEmail";
import GoogleCalendarAuth from "../GoogleCalendar/GoogleCalendarAuth";
import GoogleCalendarIcon from "../../icons/googleCalendar";
import OutlookAuth from "../Outlook/OutlookAuth";
import MicrosoftOutlookIcon from "../../icons/microsoftOutlook";
import QuoteBlock from "../Chat/QuoteBlock";
import Tooltip from "../Tooltip/Tooltip";

// File name validation function
const validateFileName = (_fileName: string): boolean => {
  // Any filename is accepted. The backend stores the original name in S3 and
  // sanitizes only the label sent to Bedrock, so special characters, unicode and
  // emoji no longer need to be blocked on the client.
  return true;
};

// Function to create a valid filename suggestion
const createValidFileNameSuggestion = (originalName: string): string => {
  const lastDotIndex = originalName.lastIndexOf('.');
  let nameWithoutExt = originalName;
  let extension = '';

  if (lastDotIndex !== -1) {
    nameWithoutExt = originalName.substring(0, lastDotIndex);
    extension = originalName.substring(lastDotIndex);
  }

  // consecutive spaces with a single space
  let validName = nameWithoutExt.replace(/\s+/g, ' ');

  // any invalid characters with underscores
  validName = validName.replace(/[^a-zA-Z0-9\s\-\(\)\[\]_]/g, '_');

  return validName + extension;
};

const MessageInput = ({
  sendMessage,
  stopMessageStream,
  loading,
  hidden = false,
  quotedText = null,
  sourceMessageId = null,
  clearQuote = () => { },
}: {
  sendMessage: (message: string, files: File[]) => void;
  stopMessageStream: () => void;
  loading: boolean;
  hidden?: boolean;
  quotedText?: string | null;
  sourceMessageId?: string | null;
  clearQuote?: () => void;
}) => {

  const {
    uploadedFiles,
    uploadProgress,
    fileStatusMap,
    uploading,
    removingFileIndex,
    handleFilesSelected,
    removeFile,
    clearUploadedFiles,
  } = useFileUpload();

  const { selectedHat, initialPromptSent } = useHat();
  const { registerFileHandler } = useFileTransfer();
  const { userId, authenticated } = useUser();
  const { getSelectedModelCapabilities } = useModel();

  const [message, setMessage] = useState("");
  const [textareaRows, setTextareaRows] = useState(1);
  const [isExpanded, setIsExpanded] = useState(false);
  const [mode, setMode] = useState<"multi" | "single">("single");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragError, setDragError] = useState<string>('');
  const dropAreaRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [formStyle, setFormStyle] = useState({});
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [lastSelectedHatId, setLastSelectedHatId] = useState<string | null>(null);
  const [showInvalidFileNameNotification, setShowInvalidFileNameNotification] = useState<boolean>(false);
  const [suggestedFileName, setSuggestedFileName] = useState<string>('');

  const [showGoogleDriveAuth, setShowGoogleDriveAuth] = useState<boolean>(false);
  const [showGoogleEmailAuth, setShowGoogleEmailAuth] = useState<boolean>(false);
  const [showGoogleCalendarAuth, setShowGoogleCalendarAuth] = useState<boolean>(false);
  const [showOutlookAuth, setShowOutlookAuth] = useState<boolean>(false);
  const [allConnectors, setAllConnectors] = useState<any[]>([]);
  const [connectorsLoading, setConnectorsLoading] = useState(true);
  const [googleDriveConnected, setGoogleDriveConnected] = useState(false);
  const [googleEmailConnected, setGoogleEmailConnected] = useState(false);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);

  const getModelCapabilities = () => {
    return getSelectedModelCapabilities();
  };

  const canUseVirtualAssistant = () => {
    const { tool_call_support } = getModelCapabilities();
    return tool_call_support;
  };

  const canUseFileUpload = () => {
    const { file_upload_support } = getModelCapabilities();
    return file_upload_support;
  };

  // Mid-conversation the BrainBank is picked from the navbar, beside the VA.
  const { brainBank: selectedBrainBank } = useComposerBrainBankSync();

  const isGoogleDriveAssistant = () => {
    if (!selectedHat || connectorsLoading || !allConnectors.length) return false;

    const hasConnectorsEnabled = selectedHat.feature_keys?.kyva_connectors?.enabled === true;
    if (!hasConnectorsEnabled) return false;

    const selectedConnectorIds = selectedHat.feature_keys?.kyva_connectors?.connector_ids || [];
    if (selectedConnectorIds.length === 0) return false;

    return selectedConnectorIds.some(connectorId => {
      const connector = allConnectors.find(c => c.id === connectorId);
      return connector && connector.status === 'active' && connector.type === 'google-drive';
    });
  };

  const isGmailAssistant = () => {
    if (!selectedHat || connectorsLoading || !allConnectors.length) return false;

    const hasConnectorsEnabled = selectedHat.feature_keys?.kyva_connectors?.enabled === true;
    if (!hasConnectorsEnabled) return false;

    const selectedConnectorIds = selectedHat.feature_keys?.kyva_connectors?.connector_ids || [];
    if (selectedConnectorIds.length === 0) return false;

    return selectedConnectorIds.some((connectorId: string) => {
      const connector = allConnectors.find((c: any) => c.id === connectorId);
      return connector && connector.status === 'active' && connector.type === 'google-email';
    });
  };

  const isGoogleCalendarAssistant = () => {
    if (!selectedHat || connectorsLoading || !allConnectors.length) return false;

    const hasConnectorsEnabled = selectedHat.feature_keys?.kyva_connectors?.enabled === true;
    if (!hasConnectorsEnabled) return false;

    const selectedConnectorIds = selectedHat.feature_keys?.kyva_connectors?.connector_ids || [];
    if (selectedConnectorIds.length === 0) return false;

    return selectedConnectorIds.some((connectorId: string) => {
      const connector = allConnectors.find((c: any) => c.id === connectorId);
      return connector && connector.status === 'active' && connector.type === 'google-calendar';
    });
  };

  const isOutlookAssistant = () => {
    if (!selectedHat || connectorsLoading || !allConnectors.length) return false;

    const hasConnectorsEnabled = selectedHat.feature_keys?.kyva_connectors?.enabled === true;
    if (!hasConnectorsEnabled) return false;

    const selectedConnectorIds = selectedHat.feature_keys?.kyva_connectors?.connector_ids || [];
    if (selectedConnectorIds.length === 0) return false;

    return selectedConnectorIds.some((connectorId: string) => {
      const connector = allConnectors.find((c: any) => c.id === connectorId);
      return connector && connector.status === 'active' && connector.type === 'microsoft-outlook';
    });
  };

  // Auto-populate message when selectedHat changes
  useEffect(() => {
    if (selectedHat?.id !== lastSelectedHatId && !initialPromptSent) {
      if (selectedHat?.initial_prompt && selectedHat.initial_prompt.trim() !== "") {
        if (!message.trim() || lastSelectedHatId) {
          setMessage(selectedHat.initial_prompt);
        }
      }
      setLastSelectedHatId(selectedHat?.id || null);
    }
  }, [selectedHat, lastSelectedHatId, initialPromptSent, message]);
  // Add this useLayoutEffect to position the form correctly
  useLayoutEffect(() => {
    const updateFormPosition = () => {
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        setFormStyle({
          position: 'fixed',
          bottom: '16px',
          left: '36px',
          width: 'calc(100% - 72px)',
          maxWidth: '100%',
          zIndex: 100
        });
      } else {
        setFormStyle({});
      }
    };

    // Run immediately
    updateFormPosition();

    // Update on resize
    window.addEventListener('resize', updateFormPosition);

    return () => {
      window.removeEventListener('resize', updateFormPosition);
    };
  }, []);

  // Focus on component mount only if not hidden
  useEffect(() => {
    if (!hidden && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [hidden]);

  // Balanced refocus logic 
  useEffect(() => {
    // Don't manage focus if component is hidden or loading
    if (hidden || loading) return;

    const timer = setTimeout(() => {
      if (textareaRef.current && document.activeElement !== textareaRef.current && !hidden) {
        const activeElement = document.activeElement;

        // Check if user is actively selecting text
        const selection = window.getSelection();
        const hasActiveSelection = selection && selection.toString().length > 0 && !selection.isCollapsed;

        // Don't refocus if user is actively selecting text
        if (hasActiveSelection) {
          return;
        }

        // Don't refocus if user is interacting with specific UI elements
        const isInteractingWithUI = activeElement && (
          activeElement.tagName === 'BUTTON' ||
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.closest('[role="dialog"]') ||
          activeElement.closest('[role="menu"]') ||
          activeElement.closest('.modal') ||
          activeElement.closest('[data-no-refocus]')
        );

        // Don't refocus if user is selecting text in message content
        const isReadingContent = activeElement && (
          activeElement.closest('[class*="message"]') ||
          activeElement.closest('[class*="chat"]') ||
          activeElement.closest('[class*="response"]') ||
          activeElement.closest('pre') ||
          activeElement.closest('code')
        ) && hasActiveSelection;

        if (!isInteractingWithUI && !isReadingContent) {
          textareaRef.current.focus();
        }
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [uploadedFiles, message, isDragging, showInvalidFileNameNotification, mode, hidden, loading]);

  // Register file handler for file transfer from File Management
  useEffect(() => {
    const fileHandler = (file: File) => {
      handleFilesSelected([file]);
    };

    registerFileHandler(fileHandler);
  }, [handleFilesSelected, registerFileHandler]);

  useEffect(() => {
    let isMounted = true;

    const fetchConnectors = async () => {
      if (!userId || !authenticated) {
        if (isMounted) {
          setConnectorsLoading(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setConnectorsLoading(true);
        }
        const response = await getConnectorInfo();

        if (isMounted) {
          setAllConnectors(response.connectors || []);
        }
      } catch (error) {
        console.error('Error fetching connectors:', error);
        if (isMounted) {
          setAllConnectors([]);
        }
      } finally {
        if (isMounted) {
          setConnectorsLoading(false);
        }
      }
    };

    fetchConnectors();

    return () => {
      isMounted = false;
    };
  }, [userId, authenticated]);

  useEffect(() => {
    let isMounted = true;

    const checkConnectionStatus = async () => {
      if (!userId || !authenticated) return;

      try {
        const [driveConnected, emailConnected, calendarConnected, outlookConn] = await Promise.all([
          checkGoogleDriveConnection().catch(() => false),
          checkGoogleEmailConnection().catch(() => false),
          checkGoogleCalendarConnection().catch(() => false),
          checkOutlookConnection().catch(() => false)
        ]);

        if (isMounted) {
          setGoogleDriveConnected(driveConnected);
          setGoogleEmailConnected(emailConnected);
          setGoogleCalendarConnected(calendarConnected);
          setOutlookConnected(outlookConn);
        }
      } catch (error) {
        console.error('Error checking connection status:', error);
      }
    };

    checkConnectionStatus();

    return () => {
      isMounted = false;
    };
  }, [userId, authenticated]);

  const handleGoogleDriveAuth = () => {
    setShowGoogleDriveAuth(true);
  };

  const handleGoogleDriveAuthClose = () => {
    setShowGoogleDriveAuth(false);
  };

  const handleGoogleDriveAuthSuccess = async (code: string) => {
    try {
      const result = await authenticateGoogleDrive(code);
      if (result.success) {
        setTimeout(async () => {
          const connected = await checkGoogleDriveConnection().catch(() => false);
          setGoogleDriveConnected(connected);
        }, 500);
      }
      setShowGoogleDriveAuth(false);
    } catch (error) {
      console.error('Error connecting Google Drive:', error);
    }
  };

  const handleGoogleEmailAuth = () => {
    setShowGoogleEmailAuth(true);
  };

  const handleGoogleEmailAuthClose = () => {
    setShowGoogleEmailAuth(false);
  };

  const handleGoogleEmailAuthSuccess = async (code: string) => {
    try {
      const result = await authenticateGoogleEmail(code);
      if (result.success) {
        setTimeout(async () => {
          const connected = await checkGoogleEmailConnection().catch(() => false);
          setGoogleEmailConnected(connected);
        }, 500);
      }
      setShowGoogleEmailAuth(false);
    } catch (error) {
      console.error('Error connecting Google Email:', error);
    }
  };

  const handleGoogleCalendarAuth = () => {
    setShowGoogleCalendarAuth(true);
  };

  const handleGoogleCalendarAuthClose = () => {
    setShowGoogleCalendarAuth(false);
  };

  const handleGoogleCalendarAuthSuccess = async (code: string) => {
    try {
      const result = await authenticateGoogleCalendar(code);
      if (result.success) {
        setTimeout(async () => {
          const connected = await checkGoogleCalendarConnection().catch(() => false);
          setGoogleCalendarConnected(connected);
        }, 500);
      }
      setShowGoogleCalendarAuth(false);
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
    }
  };

  const handleOutlookAuth = () => {
    setShowOutlookAuth(true);
  };

  const handleOutlookAuthClose = () => {
    setShowOutlookAuth(false);
  };

  const handleOutlookAuthSuccess = async (code: string) => {
    try {
      const result = await authenticateOutlook(code);
      if (result.success) {
        setTimeout(async () => {
          const connected = await checkOutlookConnection().catch(() => false);
          setOutlookConnected(connected);
        }, 500);
      }
      setShowOutlookAuth(false);
    } catch (error) {
      console.error('Error connecting Outlook:', error);
    }
  };

  // Models without file support: block drag & drop (dropEffect "none" also stops the browser opening the file)
  const fileUploadSupported = canUseFileUpload();

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!fileUploadSupported) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    setIsDragging(true);
  }, [fileUploadSupported]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!fileUploadSupported) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    if (!isDragging) setIsDragging(true);
  }, [isDragging, fileUploadSupported]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragging to false if we're leaving the drop area
    if (!dropAreaRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const validateFiles = useCallback((files: File[]) => {
    if (files.length === 0) return null;

    // Check for invalid file names
    const invalidFiles = files.filter(file => !validateFileName(file.name));

    if (invalidFiles.length > 0) {
      // Show the notification and set suggested name
      setShowInvalidFileNameNotification(true);

      // Generate a suggestion for the first invalid file
      const suggestion = createValidFileNameSuggestion(invalidFiles[0].name);
      setSuggestedFileName(suggestion);

      return `Invalid filename${invalidFiles.length > 1 ? 's' : ''}`;
    }

    return null;
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!fileUploadSupported) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const error = validateFiles(droppedFiles);

    if (error) {
      setDragError(error);
      setTimeout(() => setDragError(''), 3000);
      return;
    }

    // Filter valid files only
    const validFiles = droppedFiles.filter(file => validateFileName(file.name));
    if (validFiles.length > 0) {
      handleFilesSelected(validFiles);
    }
  }, [handleFilesSelected, validateFiles, fileUploadSupported]);

  useEffect(() => {
    const preventDefaults = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('dragenter', preventDefaults);
    document.addEventListener('dragover', preventDefaults);
    document.addEventListener('dragleave', preventDefaults);
    document.addEventListener('drop', preventDefaults);

    return () => {
      document.removeEventListener('dragenter', preventDefaults);
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('dragleave', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
    };
  }, []);


  useEffect(() => {
    if (uploadedFiles.length > 0) {
      setMode("multi");
    } else if (textareaRows >= 2 && message && mode === "single") {
      setMode("multi");
    } else if (!message && uploadedFiles.length === 0 && mode === "multi") {
      setMode("single");
    }
  }, [textareaRows, mode, message, uploadedFiles]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (message.trim() === "" && uploadedFiles.length === 0) return;

    try {
      await Promise.all(
        uploadedFiles.map((file) =>
          uploadProgress[file.name] === 100 ||
            uploadProgress[file.name] === "failed"
            ? null
            : handleFilesSelected([file])
        )
      );

      sendMessage(message.trim(), uploadedFiles);
      setMessage("");
      if (uploadedFiles.length > 0) clearUploadedFiles();
      if (quotedText && clearQuote) clearQuote();

      // Ensure focus returns to textarea after successful submission
      setTimeout(() => {
        if (textareaRef.current && !hidden) {
          textareaRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.info("Error sending message")
    }
  };

  const isAnyFileProcessing = uploadedFiles.some(
    (file) => fileStatusMap[file.name] === "processing"
  );

  // Handle manual message changes (user typing)
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  // Minimal blur handler
  const handleTextareaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Don't aggressively refocus - let users interact freely
  };

  // Dictate handler
  const handleDictateTranscription = (text: string) => {
    setMessage(prev => prev + (prev ? " " : "") + text);
    // Focus back to textarea after transcription
    setTimeout(() => {
      if (textareaRef.current && !hidden) {
        textareaRef.current.focus();
      }
    }, 100);
  };

  // Add this early return to hide component when needed
  if (hidden) {
    return null;
  }

  return (
    <form
      ref={formRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onSubmit={handleFormSubmit}
      onKeyDown={(e) => {
        if (e.key === "Escape" && isExpanded) {
          e.preventDefault();
          setIsExpanded(false);
          setTimeout(() => textareaRef.current?.focus(), 10);
        }
        if (e.key === "Enter" && !e.shiftKey && !loading) {
          e.preventDefault();
          const isInputDisabled =
            (message.trim().length === 0 && uploadedFiles.length === 0 && !quotedText) ||
            uploading ||
            isAnyFileProcessing;
          if (!isInputDisabled) {
            handleFormSubmit(e);
          }
        }
      }}
      className={mergeTailwindClasses(
        "bg-surface-overlay border border-border-strong shadow-sm text-primary px-5 pt-4 pb-3 flex overflow-hidden relative transition-all duration-200 ease-in-out focus-within:border-transparent focus-within:shadow-md focus-within:ring-1 focus-within:ring-violet-500/20",
        window.innerWidth <= 768 ? "" : "w-full", // Only apply w-full on desktop
        mode === "multi" ? "flex-col rounded-2xl" : "flex-row rounded-2xl items-end"
      )}
      style={formStyle}
    >
      {/* Show selected VA and BrainBank indicator */}
      {(selectedHat || selectedBrainBank) && (
        <div className="absolute -top-8 left-0 right-0 px-2 py-1 bg-violet-100 dark:bg-violet-500/20 rounded-md text-xs text-violet-700 dark:text-violet-200 flex items-center justify-between">
          <span className="truncate">
            {selectedHat && <>Using VA: <strong>{selectedHat.hat_name}</strong></>}
            {selectedHat && selectedBrainBank && " · "}
            {selectedBrainBank && <>Using BrainBank: <strong>{selectedBrainBank.name}</strong></>}
          </span>
          {selectedHat?.initial_prompt && (
            <span className="text-violet-500 shrink-0 ml-2">Initial prompt loaded</span>
          )}
        </div>
      )}

      {mode === "single" && canUseFileUpload() && <EnhancedAttach onFilesSelected={handleFilesSelected} />}

      {/* Quote Block */}
      {quotedText && (
        <div className="w-full mb-2 px-2">
          <QuoteBlock text={quotedText} onClear={clearQuote || (() => { })} />
        </div>
      )}

      <div className="relative flex-grow flex-shrink w-full group">
        <TextareaAutosize
          ref={textareaRef}
          value={message}
          onChange={handleMessageChange}
          onBlur={handleTextareaBlur}
          onHeightChange={(height, props) => {
            setTextareaRows(Math.ceil(height / props.rowHeight));
          }}
          className={mergeTailwindClasses(
            "transition-all duration-200 ease-in-out bg-transparent placeholder:text-subtle text-[15px] leading-6 font-normal resize-none focus:outline-none w-full px-2 pr-8 mb-1.5",
            isExpanded ? "!h-[50vh] !max-h-[50vh] overflow-y-auto" : "max-h-[120px] overflow-y-auto"
          )}

          placeholder="Ask a follow-up"
          disabled={loading}
        />
        <Tooltip label={isExpanded ? "Collapse (Esc)" : "Expand"} side="left">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsExpanded((prev) => !prev);
              setTimeout(() => textareaRef.current?.focus(), 10);
            }}
            className={mergeTailwindClasses(
              "absolute top-0 right-2 p-1 text-subtle hover:text-primary transition-opacity bg-surface-2 hover:bg-surface-3 rounded-md focus:outline-none",
              isExpanded ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            )}
          >
            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </Tooltip>
      </div>
      {uploadedFiles.length > 0 && (
        <div className="grid grid-cols-2 gap-2 my-2 w-full">
          {uploadedFiles.map((file, index) => (
            <div
              key={index}
              className="relative flex flex-col items-start overflow-hidden rounded-xl border border-border-default p-2 bg-surface-2"
            >
              <div className="relative flex items-center w-full">
                <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md bg-surface-3 flex items-center justify-center text-xs">
                  <span className="text-primary text-[10px]">
                    <FileIcon file={{ file_name: file.name }} />
                  </span>
                </div>
                <div className="overflow-hidden flex-grow px-2">
                  <div className="truncate font-semibold text-xs pr-4">
                    {getDisplayFileName(file.name)}
                  </div>
                  <div className="truncate text-xs">
                    {formatFileSize(file.size)}
                  </div>
                </div>
              </div>
              {uploadProgress[file.name] !== undefined && (
                <div className="w-full flex items-center mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div
                      className={`${uploadProgress[file.name] === "failed"
                        ? "bg-red-500"
                        : "bg-green-500"
                        } h-1 rounded-full`}
                      style={{
                        width: `${uploadProgress[file.name] === "failed"
                          ? "100%"
                          : `${uploadProgress[file.name] || 0}%`
                          }`,
                      }}
                    ></div>
                  </div>
                  <div className="ml-2 text-xs text-gray-500 flex items-center gap-1">
                    {typeof uploadProgress[file.name] === "number"
                      ? `${(uploadProgress[file.name] as number).toFixed(2)}%`
                      : "ERROR"}
                    {fileStatusMap[file.name] === "processed" && <CheckCircle size={"12"} className="text-green-500" />}
                  </div>
                </div>
              )}
              <button
                type="button"
                className="absolute top-2 right-2 transform rounded-full border p-0.5 transition-colors hover:opacity-100"
                onClick={() => removeFile(index)}
                disabled={
                  removingFileIndex === index ||
                  fileStatusMap[file.name] === "processing"
                }
              >
                {removingFileIndex === index ? (
                  <LoaderCircle
                    size={"17"}
                    className="animate-spin text-red-600"
                  />
                ) : fileStatusMap[file.name] === "processing" ? (
                  <Cog size={"17"} className="animate-spin text-blue-500" />
                ) : (
                  <XCircle size={"17"} className="text-red-600" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
      <BrainBankSyncStrip hasAttachments={uploadedFiles.length > 0} />
      {mode === "single" && (
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="hidden sm:flex items-center space-x-4">
            {canUseVirtualAssistant() && isGmailAssistant() && (
              <Tooltip label={googleEmailConnected ? "Gmail connected" : "Gmail not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleEmailAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleEmailIcon size={18} />
                  {!googleEmailConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            {canUseVirtualAssistant() && isGoogleCalendarAssistant() && (
              <Tooltip label={googleCalendarConnected ? "Google Calendar connected" : "Google Calendar not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleCalendarAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleCalendarIcon size={18} />
                  {!googleCalendarConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            {canUseVirtualAssistant() && isOutlookAssistant() && (
              <Tooltip label={outlookConnected ? "Outlook connected" : "Outlook not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleOutlookAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <MicrosoftOutlookIcon size={18} />
                  {!outlookConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            {canUseVirtualAssistant() && isGoogleDriveAssistant() && (
              <Tooltip label={googleDriveConnected ? "Google Drive connected" : "Google Drive not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleDriveAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleDriveIcon size={18} />
                  {!googleDriveConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            <DictateComponent
              onTranscription={handleDictateTranscription}
              disabled={uploading || isAnyFileProcessing || loading}
            />
            <div>
              {loading ? (
                <button
                  className="bg-gradient-to-br from-violet-600 to-violet-700 text-white hover:from-violet-700 hover:to-violet-800 transition-all duration-150 rounded-full inline-flex items-center justify-center h-9 w-9"
                  onClick={(e) => {
                    e.preventDefault();
                    stopMessageStream();
                  }}
                >
                  <Square
                    strokeWidth={"3px"}
                    className="bg-background"
                    size={14}
                    fill="white"
                  />
                </button>
              ) : (
                <button
                  disabled={
                    (message.trim().length === 0 && uploadedFiles.length === 0 && !quotedText) ||
                    uploading ||
                    isAnyFileProcessing
                  }
                  className="bg-gradient-to-br from-violet-600 to-violet-700 text-white disabled:text-subtle hover:from-violet-700 hover:to-violet-800 disabled:bg-none disabled:bg-surface-3 disabled:cursor-not-allowed transition-all duration-150 rounded-full inline-flex items-center justify-center h-9 w-9"
                >
                  <ArrowUp className="bg-background" size={17} />
                </button>
              )}
            </div>
          </div>

          {/* Mobile: First row - connector buttons and send button */}
          <div className="flex sm:hidden justify-between items-center">
            <div className="flex items-center space-x-2">
              {canUseVirtualAssistant() && isGmailAssistant() && (
                <Tooltip label={googleEmailConnected ? "Gmail connected" : "Gmail not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleEmailAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleEmailIcon size={18} />
                    {!googleEmailConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {canUseVirtualAssistant() && isGoogleCalendarAssistant() && (
                <Tooltip label={googleCalendarConnected ? "Google Calendar connected" : "Google Calendar not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleCalendarAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleCalendarIcon size={18} />
                    {!googleCalendarConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {canUseVirtualAssistant() && isOutlookAssistant() && (
                <Tooltip label={outlookConnected ? "Outlook connected" : "Outlook not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleOutlookAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <MicrosoftOutlookIcon size={18} />
                    {!outlookConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {canUseVirtualAssistant() && isGoogleDriveAssistant() && (
                <Tooltip label={googleDriveConnected ? "Google Drive connected" : "Google Drive not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleDriveAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleDriveIcon size={18} />
                    {!googleDriveConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}
            </div>
            {loading ? (
              <button
                className="bg-gradient-to-br from-violet-600 to-violet-700 text-white hover:from-violet-700 hover:to-violet-800 transition-all duration-150 rounded-full inline-flex items-center justify-center h-9 w-9"
                onClick={(e) => {
                  e.preventDefault();
                  stopMessageStream();
                }}
              >
                <Square
                  strokeWidth={"3px"}
                  className="bg-background"
                  size={14}
                  fill="white"
                />
              </button>
            ) : (
              <button
                disabled={
                  (message.trim().length === 0 && uploadedFiles.length === 0 && !quotedText) ||
                  uploading ||
                  isAnyFileProcessing
                }
                className="bg-gradient-to-br from-violet-600 to-violet-700 text-white disabled:text-subtle hover:from-violet-700 hover:to-violet-800 disabled:bg-none disabled:bg-surface-3 disabled:cursor-not-allowed transition-all duration-150 rounded-full inline-flex items-center justify-center h-9 w-9"
              >
                <ArrowUp className="bg-background" size={17} />
              </button>
            )}
          </div>

          {/* Mobile: Dictate component in second row */}
          <div className="flex sm:hidden justify-end">
            <DictateComponent
              onTranscription={handleDictateTranscription}
              disabled={uploading || isAnyFileProcessing || loading}
            />
          </div>
        </div>
      )}
      {mode === "multi" && (
        <div className="flex flex-col sm:flex-row-reverse sm:items-center sm:justify-between w-full pt-2 space-y-2 sm:space-y-0">
          {/* Desktop: All buttons in one row */}
          <div className="hidden sm:flex flex-row items-center space-x-4">
            {canUseVirtualAssistant() && isGmailAssistant() && (
              <Tooltip label={googleEmailConnected ? "Gmail connected" : "Gmail not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleEmailAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleEmailIcon size={18} />
                  {!googleEmailConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            {canUseVirtualAssistant() && isGoogleCalendarAssistant() && (
              <Tooltip label={googleCalendarConnected ? "Google Calendar connected" : "Google Calendar not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleCalendarAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleCalendarIcon size={18} />
                  {!googleCalendarConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            {canUseVirtualAssistant() && isOutlookAssistant() && (
              <Tooltip label={outlookConnected ? "Outlook connected" : "Outlook not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleOutlookAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <MicrosoftOutlookIcon size={18} />
                  {!outlookConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            {canUseVirtualAssistant() && isGoogleDriveAssistant() && (
              <Tooltip label={googleDriveConnected ? "Google Drive connected" : "Google Drive not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleDriveAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleDriveIcon size={18} />
                  {!googleDriveConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            {canUseFileUpload() && <EnhancedAttach onFilesSelected={handleFilesSelected} />}
            <div>
              {loading ? (
                <button
                  className="bg-gradient-to-br from-violet-600 to-violet-700 text-white hover:from-violet-700 hover:to-violet-800 transition-all duration-150 rounded-full inline-flex items-center justify-center h-9 w-9"
                  onClick={(e) => {
                    e.preventDefault();
                    stopMessageStream();
                  }}
                >
                  <Square
                    strokeWidth={"3px"}
                    className="bg-background"
                    size={14}
                    fill="white"
                  />
                </button>
              ) : (
                <div className="flex flex-row items-center space-x-4 -mx-2">
                  <DictateComponent
                    onTranscription={handleDictateTranscription}
                    disabled={uploading || isAnyFileProcessing || loading}
                  />
                  <button
                    disabled={
                      (message.trim().length === 0 && uploadedFiles.length === 0 && !quotedText) ||
                      uploading ||
                      isAnyFileProcessing
                    }
                    className="bg-gradient-to-br from-violet-600 to-violet-700 text-white disabled:text-subtle hover:from-violet-700 hover:to-violet-800 disabled:bg-none disabled:bg-surface-3 disabled:cursor-not-allowed transition-all duration-150 rounded-full inline-flex items-center justify-center text-sm font-medium h-9 w-9"
                  >
                    <ArrowUp className="bg-background" size={17} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile: First row - connector buttons, attach and send buttons */}
          <div className="flex sm:hidden justify-between items-center w-full">
            <div className="flex items-center space-x-2">
              {canUseVirtualAssistant() && isGmailAssistant() && (
                <Tooltip label={googleEmailConnected ? "Gmail connected" : "Gmail not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleEmailAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleEmailIcon size={18} />
                    {!googleEmailConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {canUseVirtualAssistant() && isGoogleCalendarAssistant() && (
                <Tooltip label={googleCalendarConnected ? "Google Calendar connected" : "Google Calendar not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleCalendarAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleCalendarIcon size={18} />
                    {!googleCalendarConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {canUseVirtualAssistant() && isOutlookAssistant() && (
                <Tooltip label={outlookConnected ? "Outlook connected" : "Outlook not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleOutlookAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <MicrosoftOutlookIcon size={18} />
                    {!outlookConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {canUseVirtualAssistant() && isGoogleDriveAssistant() && (
                <Tooltip label={googleDriveConnected ? "Google Drive connected" : "Google Drive not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleDriveAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleDriveIcon size={18} />
                    {!googleDriveConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

                {canUseFileUpload() && <EnhancedAttach onFilesSelected={handleFilesSelected} />}
            </div>

            <div>
              {loading ? (
                <button
                  className="bg-gradient-to-br from-violet-600 to-violet-700 text-white hover:from-violet-700 hover:to-violet-800 transition-all duration-150 rounded-full inline-flex items-center justify-center h-9 w-9"
                  onClick={(e) => {
                    e.preventDefault();
                    stopMessageStream();
                  }}
                >
                  <Square
                    strokeWidth={"3px"}
                    className="bg-background"
                    size={14}
                    fill="white"
                  />
                </button>
              ) : (
                <button
                  disabled={
                    (message.trim().length === 0 && uploadedFiles.length === 0 && !quotedText) ||
                    uploading ||
                    isAnyFileProcessing
                  }
                  className="bg-gradient-to-br from-violet-600 to-violet-700 text-white disabled:text-subtle hover:from-violet-700 hover:to-violet-800 disabled:bg-none disabled:bg-surface-3 disabled:cursor-not-allowed transition-all duration-150 rounded-full inline-flex items-center justify-center text-sm font-medium h-9 w-9"
                >
                  <ArrowUp className="bg-background" size={17} />
                </button>
              )}
            </div>
          </div>

          {/* Mobile: Second row - Dictate component */}
          <div className="flex sm:hidden justify-end">
            <DictateComponent
              onTranscription={handleDictateTranscription}
              disabled={uploading || isAnyFileProcessing || loading}
            />
          </div>
        </div>
      )}
      {isDragging && (
        <div
          ref={dropAreaRef}
          className="absolute inset-0 z-50 flex items-center justify-center bg-violet-100/90 border-2 border-dashed border-violet-500 rounded-lg"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center bg-white p-6 rounded-xl shadow-lg">
            <div className="mb-2">
              <Paperclip className="h-10 w-10 mx-auto text-violet-600" />
            </div>
            <p className="text-violet-800 font-medium text-lg">Drop files here</p>
            <p className="text-violet-600 text-sm">Drag and drop multiple files</p>
          </div>
        </div>
      )}

      {dragError && (
        <div className="absolute bottom-full mb-2 right-0 bg-red-100 text-red-700 px-3 py-1 rounded-lg flex items-center text-sm">
          <AlertCircle size={16} className="mr-1" />
          {dragError}
        </div>
      )}
      {/* Invalid filename notification */}
      {showInvalidFileNameNotification && (
        <div className="fixed bottom-[70px] left-0 right-0 mx-auto w-[calc(100%-16px)] max-w-2xl z-50 px-2 sm:px-4" style={{ animation: 'fadeIn 0.3s ease-out forwards' }}>
          <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-md p-2 sm:p-3 flex flex-col">
            <div className="flex items-start justify-between">
              <div className="flex items-start sm:items-center space-x-2">
                <AlertCircle className="text-amber-600 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-1 sm:mt-0.5" />
                <p className="text-xs sm:text-sm text-amber-800">
                  File names can only contain alphanumeric characters, spaces, hyphens, parentheses,
                  and square brackets. No consecutive spaces allowed.
                </p>
              </div>
              <button
                onClick={() => setShowInvalidFileNameNotification(false)}
                className="text-amber-600 hover:text-amber-800 rounded-full p-1 transition-colors ml-1 sm:ml-2 flex-shrink-0"
                aria-label="Close notification"
              >
                <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
            {suggestedFileName && (
              <div className="mt-1.5 sm:mt-2 text-xs sm:text-sm">
                <p className="font-medium text-amber-800">Suggested filename:</p>
                <div className="bg-white p-1 sm:p-1.5 rounded border border-amber-200 mt-1 text-gray-700 font-mono break-all text-xs sm:text-sm">
                  {suggestedFileName}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <GoogleDriveAuth
        isOpen={showGoogleDriveAuth}
        onClose={handleGoogleDriveAuthClose}
        onAuthSuccess={handleGoogleDriveAuthSuccess}
      />

      <GoogleEmailAuth
        isOpen={showGoogleEmailAuth}
        onClose={handleGoogleEmailAuthClose}
        onAuthSuccess={handleGoogleEmailAuthSuccess}
      />

      <GoogleCalendarAuth
        isOpen={showGoogleCalendarAuth}
        onClose={handleGoogleCalendarAuthClose}
        onAuthSuccess={handleGoogleCalendarAuthSuccess}
      />

      <OutlookAuth
        isOpen={showOutlookAuth}
        onClose={handleOutlookAuthClose}
        onAuthSuccess={handleOutlookAuthSuccess}
      />
    </form>
  );
};

export default MessageInput;